// Multi-territory Compliance Master API service.
// Mirrors the India Compliance Master data-access module
// (src/api/ComplianceMasterApis.js) using the MT api-utils helpers.
//
// The compliance-master CRUD endpoints are shared with India — this service
// reuses the India COMPLIANCE_MASTER_* endpoints (wht-admin/api/v1/compliance-master/*)
// rather than defining MT-specific ones. Entity Name / Entity TAN options are
// loaded from the Assign-Entity dropdown APIs for every country (India
// included) in the component.

import {
    COMPLIANCE_MASTER_BASE_API,
    COMPLIANCE_MASTER_CATEGORIES_API,
    COMPLIANCE_MASTER_CREATE_API,
    COMPLIANCE_MASTER_DELETE_API,
    COMPLIANCE_MASTER_DOWNLOAD_API,
    COMPLIANCE_MASTER_LIST_API,
} from '@/endpoints/apiEndpoints'
import {
    MTApiResponse,
    mtBuildRequestHeader,
    mtHandleErrorResponse,
    mtHandleRequestFailure,
    mtParseJsonResponse,
    mtParseMessageResponse,
    mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'

// --- Types ---

export interface MTComplianceListRequest {
  pageFilter: {
    currentPage: number
    includeCount?: boolean
    pageSize: number
    sortField?: string
    sortOrder?: 'ASC' | 'DESC'
  }
  requestFilter?: unknown[]
  isReportDownload?: boolean
  searchCOList?: unknown[]
}

export interface MTComplianceCreateRequest {
  countryCode: string
  countryName: string
  complianceCategories: string[]
  // Identifier scoping mirrors the MT Key Filter panel `tanApplicable` pattern:
  // when TAN (tax registration) is enabled for the country only `tanUuids` is
  // sent; otherwise only `entityUuids` is sent. Both are optional so the
  // non-applicable one can be omitted from the request body.
  entityUuids?: string[]
  tanUuids?: string[]
  startDate: string
}

// --- Table list -----------------------------------------------------------
export const getComplianceEntryList = async <T = unknown>(
  payload: MTComplianceListRequest
): Promise<MTApiResponse<T>> => {
  const url = mtUrlBuilder(COMPLIANCE_MASTER_LIST_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<T>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Compliance categories (for the Add modal) ----------------------------
// Categories are country-scoped — pass the selected `countryCode` so the
// backend returns only the compliance categories valid for that territory
// (e.g. .../categories?countryCode=IND). Omitted when no country is selected.
export const getComplianceCategoryList = async <T = unknown>(
  countryCode?: string
): Promise<MTApiResponse<T>> => {
  const url = mtUrlBuilder(
    COMPLIANCE_MASTER_CATEGORIES_API,
    {},
    { countryCode: countryCode || undefined }
  )
  const requestInit = mtBuildRequestHeader('GET')
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<T>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Create ---------------------------------------------------------------
export const createComplianceEntry = async <T = unknown>(
  payload: MTComplianceCreateRequest
): Promise<MTApiResponse<T>> => {
  const url = mtUrlBuilder(COMPLIANCE_MASTER_CREATE_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<T>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Update completion date -----------------------------------------------
// PATCH `${BASE}/compliance-master/{uuid}/completion-date` body { completionDate }
export const updateComplianceEntry = async (
  uuid: string,
  updates: { completionDate: string }
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(
    `${COMPLIANCE_MASTER_BASE_API}/${uuid}/completion-date`
  )
  const requestInit = mtBuildRequestHeader('PATCH', {
    completionDate: updates.completionDate,
  })
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Delete ---------------------------------------------------------------
// Sends an array of uuids in the request body.
export const deleteComplianceEntries = async (
  uuids: string[]
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(COMPLIANCE_MASTER_DELETE_API)
  const requestInit = mtBuildRequestHeader('POST', uuids)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Download (report request) --------------------------------------------
export const downloadComplianceExcel = async (
  payload: MTComplianceListRequest
): Promise<MTApiResponse<Blob>> => {
  const url = mtUrlBuilder(COMPLIANCE_MASTER_DOWNLOAD_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
